<template>
  <div class="flex-1 overflow-auto p-4 flex justify-between flex-col">
    <router-link
      :to="addAccountScoping('settings/email_templates/new')"
      class="button success button--fixed-top flex gap-1"
    >
      <fluent-icon icon="add-circle" />
      <span class="button__content">
        {{ $t('EMAIL_TEMPLATES.HEADER_BTN_TXT') }}
      </span>
    </router-link>

    <div class="divone">
      <div class="divtwo">
        <!-- Filter Tabs + Search (like WhatsApp template list) -->
        <div class="main-header">
          <div class="header-left">
            <div class="filter-tabs">
              <button
                v-for="tab in filterTabs"
                :key="tab.key"
                class="filter-tab"
                :class="{ 'filter-tab--active': activeFilter === tab.key }"
                @click="onFilterChange(tab.key)"
              >
                {{ tab.label }}
              </button>
            </div>
            <div class="template-search-wrap">
              <div class="search-icon-wrap" @click="applySearch">
                <fluent-icon icon="search" class="search-icon" />
              </div>
              <input
                type="text"
                :placeholder="$t('EMAIL_TEMPLATES.LIST.SEARCH_PLACEHOLDER')"
                class="template-search-input"
                :value="searchQuery"
                @keyup.enter="applySearch"
                @input="searchQuery = $event.target.value"
              />
            </div>
          </div>
        </div>

        <div v-if="!uiFlags.isFetching && filteredTemplates.length" class="table-wrapper">
          <table class="whatsapp">
            <thead>
              <tr>
                <th>{{ $t('EMAIL_TEMPLATES.LIST.NAME') }}</th>
                <th>{{ $t('EMAIL_TEMPLATES.LIST.SUBJECT') }}</th>
                <th>{{ $t('EMAIL_TEMPLATES.LIST.VISIBILITY') }}</th>
                <th>{{ $t('EMAIL_TEMPLATES.LIST.UPDATED') }}</th>
                <th class="text-center">{{ $t('EMAIL_TEMPLATES.LIST.ACTIONS') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="template in paginatedTemplates" :key="template.id">
                <td data-label="NAME">
                  <span class="font-medium text-slate-900 dark:text-slate-100">
                    {{ template.name }}
                  </span>
                </td>
                <td data-label="SUBJECT" class="max-w-xs truncate text-slate-600 dark:text-slate-400">
                  {{ template.subject }}
                </td>
                <td data-label="VISIBILITY">
                  <span
                    class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                    :class="
                      template.visibility === 'global'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-slate-100 text-slate-700 dark:bg-slate-600 dark:text-slate-300'
                    "
                  >
                    {{
                      template.visibility === 'global'
                        ? $t('EMAIL_TEMPLATES.VISIBILITY.PUBLIC')
                        : $t('EMAIL_TEMPLATES.VISIBILITY.PRIVATE')
                    }}
                  </span>
                </td>
                <td data-label="UPDATED" class="text-slate-500 text-sm">
                  {{ formatDate(template.updated_at) }}
                </td>
                <td data-label="ACTION" class="action-cell">
                  <div class="action-buttons flex justify-center gap-2">
                    <woot-button
                      v-tooltip.top-end="$t('EMAIL_TEMPLATES.VIEW_TOOLTIP')"
                      size="tiny"
                      variant="smooth"
                      icon="eye-show"
                      color-scheme="primary"
                      @click="viewTemplate(template)"
                    />
                    <woot-button
                      v-tooltip.top-end="$t('EMAIL_TEMPLATES.EDIT_TOOLTIP')"
                      size="tiny"
                      variant="smooth"
                      icon="edit"
                      color-scheme="secondary"
                      @click="editTemplate(template)"
                    />
                    <woot-button
                      v-tooltip.top-end="$t('EMAIL_TEMPLATES.PREVIEW_TOOLTIP')"
                      size="tiny"
                      variant="smooth"
                      icon="document"
                      @click="openPreview(template)"
                    />
                    <woot-button
                      v-tooltip.top-end="$t('EMAIL_TEMPLATES.DELETE_TOOLTIP')"
                      size="tiny"
                      variant="smooth"
                      icon="delete"
                      color-scheme="alert"
                      @click="openDeletePopup(template)"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p
          v-if="!uiFlags.isFetching && !filteredTemplates.length"
          class="no-data text-center"
        >
          {{ templates.length ? $t('EMAIL_TEMPLATES.LIST.NO_MATCH') : $t('EMAIL_TEMPLATES.LIST.404') }}
        </p>

        <woot-loading-state
          v-if="uiFlags.isFetching"
          :message="$t('EMAIL_TEMPLATES.LOADING')"
        />
      </div>
    </div>

    <!-- Sticky footer (like WhatsApp template table) -->
    <div
      v-if="!uiFlags.isFetching && filteredTemplates.length > 0"
      class="table-footer-wrap table-footer-sticky"
    >
      <table-footer
        :current-page="currentPage"
        :page-size="perPage"
        :total-count="filteredTemplates.length"
        @page-change="onPageChange"
      />
    </div>

    <woot-delete-modal
      :show.sync="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('EMAIL_TEMPLATES.DELETE.CONFIRM.TITLE')"
      :message="$t('EMAIL_TEMPLATES.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="$t('EMAIL_TEMPLATES.DELETE.CONFIRM.YES')"
      :reject-text="$t('EMAIL_TEMPLATES.DELETE.CONFIRM.NO')"
    />
    <woot-modal
      content-class="email-template-preview-modal-container"
      :show.sync="showPreviewModal"
      :on-close="closePreview"
    >
      <email-template-preview-content
        v-if="previewData"
        :subject="previewData.subject"
        :body-html="formattedPreviewBody"
        :attachments="previewData.attachments || []"
      />
    </woot-modal>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import accountMixin from 'dashboard/mixins/account.js';
import EmailTemplatesAPI from 'dashboard/api/emailTemplates';
import MessageFormatter from 'shared/helpers/MessageFormatter';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import EmailTemplatePreviewContent from './EmailTemplatePreviewContent.vue';

export default {
  components: { TableFooter, EmailTemplatePreviewContent },
  mixins: [alertMixin, accountMixin],
  data() {
    return {
      templates: [],
      currentPage: 1,
      perPage: 20,
      uiFlags: { isFetching: false },
      showDeleteConfirmationPopup: false,
      selectedTemplate: null,
      showPreviewModal: false,
      previewData: null,
      searchQuery: '',
      activeFilter: 'all',
      filterTabs: [
        { key: 'all', label: 'All' },
        { key: 'global', label: 'Public' },
        { key: 'private', label: 'Private' },
      ],
    };
  },
  computed: {
    formattedPreviewBody() {
      if (!this.previewData?.body) return '';
      return new MessageFormatter(this.previewData.body).formattedMessage;
    },
    filteredTemplates() {
      let list = this.templates;
      if (this.activeFilter === 'global') {
        list = list.filter(t => t.visibility === 'global');
      } else if (this.activeFilter === 'private') {
        list = list.filter(t => t.visibility !== 'global');
      }
      const q = this.searchQuery.trim().toLowerCase();
      if (q) {
        list = list.filter(
          t =>
            (t.name && t.name.toLowerCase().includes(q)) ||
            (t.subject && t.subject.toLowerCase().includes(q))
        );
      }
      return list;
    },
    paginatedTemplates() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredTemplates.slice(start, start + this.perPage);
    },
  },
  watch: {
    activeFilter() {
      this.currentPage = 1;
    },
    searchQuery() {
      this.currentPage = 1;
    },
  },
  mounted() {
    this.fetchTemplates();
  },
  methods: {
    applySearch() {
      this.currentPage = 1;
    },
    onFilterChange(key) {
      this.activeFilter = key;
      this.currentPage = 1;
    },
    onPageChange(page) {
      this.currentPage = page;
    },
    async fetchTemplates() {
      this.uiFlags.isFetching = true;
      try {
        const { data } = await EmailTemplatesAPI.get();
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];
        this.templates = list;
        this.currentPage = 1;
      } catch (e) {
        this.templates = [];
        this.showAlert(this.$t('EMAIL_TEMPLATES.ERROR_LOAD'));
      } finally {
        this.uiFlags.isFetching = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return '';
      try {
        return d.toLocaleString(undefined, {
          dateStyle: 'short',
          timeStyle: 'short',
        });
      } catch {
        return (
          d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
          ' ' +
          d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
        );
      }
    },
    viewTemplate(template) {
      this.$router.push(
        this.addAccountScoping(`settings/email_templates/${template.id}/view`)
      );
    },
    editTemplate(template) {
      this.$router.push(
        this.addAccountScoping(`settings/email_templates/${template.id}/edit`)
      );
    },
    async openPreview(template) {
      try {
        const { data } = await EmailTemplatesAPI.getPreview(template.id);
        this.previewData = data;
        this.showPreviewModal = true;
      } catch (e) {
        this.showAlert(this.$t('EMAIL_TEMPLATES.PREVIEW_ERROR'));
      }
    },
    closePreview() {
      this.showPreviewModal = false;
      this.previewData = null;
    },
    openDeletePopup(template) {
      this.selectedTemplate = template;
      this.showDeleteConfirmationPopup = true;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
      this.selectedTemplate = null;
    },
    async confirmDeletion() {
      if (!this.selectedTemplate) return;
      try {
        await EmailTemplatesAPI.delete(this.selectedTemplate.id);
        this.templates = this.templates.filter(
          t => t.id !== this.selectedTemplate.id
        );
        const maxPage = Math.max(
          1,
          Math.ceil(this.filteredTemplates.length / this.perPage)
        );
        if (this.currentPage > maxPage) this.currentPage = maxPage;
        this.showAlert(this.$t('EMAIL_TEMPLATES.DELETE.SUCCESS'));
      } catch (e) {
        this.showAlert(this.$t('EMAIL_TEMPLATES.DELETE.ERROR'));
      }
      this.closeDeletePopup();
    },
  },
};
</script>

<style lang="scss" scoped>
.divone {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  @apply p-3 bg-white dark:bg-slate-900;
}

.divtwo {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

/* Match WhatsApp templates list: pill filters + search (Index.vue) */
.main-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 16px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
}

.filter-tabs {
  display: inline-flex;
  align-items: center;
  padding: 4px;
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 9999px;
  gap: 2px;
  flex-shrink: 0;

  @apply dark:bg-slate-900 dark:border-slate-700;
}

.filter-tab {
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  border-radius: 9999px;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  @apply dark:text-slate-300;

  &:hover {
    background-color: #f3f4f6;
    color: #374151;
    @apply dark:bg-slate-800 dark:text-slate-200;
  }

  &.filter-tab--active {
    background-color: #40518a;
    color: white;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

    @apply dark:bg-woot-500;

    &:hover {
      background-color: #344270;
      color: white;
      @apply dark:bg-woot-500;
    }
  }
}

.template-search-wrap {
  position: relative;
  display: flex;
  align-items: center;
  width: 320px;
  max-width: 100%;
  flex-shrink: 1;

  .search-icon-wrap {
    position: absolute;
    right: 12px;
    display: flex;
    align-items: center;
    height: 100%;
    cursor: pointer;
    z-index: 1;
    color: #94a3b8;

    @apply dark:text-slate-400;

    :deep(svg) {
      height: 18px;
      width: 18px;
    }
  }

  .template-search-input {
    width: 100%;
    height: 38px;
    padding: 0 40px 0 12px;
    font-size: 13px;
    border: 1px solid #ccc;
    border-radius: 6px;
    background: white;
    margin: 0;
    box-sizing: border-box;

    @apply dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200;

    &::placeholder {
      color: #9ca3af;
      @apply dark:text-slate-500;
    }

    &:focus {
      outline: none;
      border-color: #1f93ff;
      box-shadow: 0 0 0 1px #1f93ff;
    }
  }
}

@media (max-width: 768px) {
  .main-header {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .header-left {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .filter-tabs {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 4px;
    justify-content: flex-start;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .template-search-wrap {
    width: 100%;
  }
}

.table-wrapper {
  width: 100%;
  margin-bottom: 16px;

  @media (min-width: 769px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

table.whatsapp {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background-color: white;
  border: 1px solid #cfd2d6;
  border-radius: 8px;
  overflow: hidden;

  @apply dark:bg-slate-900 dark:border-slate-700;

  thead th {
    padding: var(--space-small, 8px) var(--space-two, 16px);
    font-size: var(--font-size-mini, 12px);
    background-color: #f9fafb;
    border-bottom: 1px solid #cfd2d6;
    border-right: 1px solid #cfd2d6;
    text-align: left;
    font-weight: 600;
    color: #999;
    white-space: nowrap;
    text-transform: uppercase;
    letter-spacing: 1px;
    line-height: 1.3;

    @apply dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700;

    &.text-center {
      text-align: center;
    }

    &:last-child {
      border-right: none;
    }
  }

  tbody tr {
    transition: background-color 0.15s ease;

    &:hover td {
      background-color: #f9fafb;
      @apply dark:bg-slate-800;
    }

    &:last-child td {
      border-bottom: none;
    }
  }

  tbody td {
    padding: 6px 16px;
    border-bottom: 1px solid #cfd2d6;
    border-right: 1px solid #cfd2d6;
    vertical-align: middle;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    line-height: 1.3;
    color: #333;
    background-color: white;

    @apply dark:text-slate-200 dark:bg-slate-900 dark:border-slate-700;

    &:last-child {
      border-right: none;
    }
  }

  @media (max-width: 768px) {
    border: none;
    border-radius: 0;

    thead {
      display: none;
    }

    tbody tr {
      display: block;
      margin-bottom: 16px;
      border: 1px solid #cfd2d6;
      border-radius: 8px;
      background-color: white;
      @apply dark:border-slate-700 dark:bg-slate-900;

      &:last-child {
        margin-bottom: 0;
      }
    }

    tbody td {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 16px;
      border: none;
      border-bottom: 1px solid #e5e7eb;
      text-align: right;
      white-space: normal;
      word-break: break-word;
      @apply dark:border-slate-700;

      &:last-child {
        border-bottom: none;
      }

      &::before {
        content: attr(data-label);
        font-weight: 600;
        text-transform: uppercase;
        font-size: 11px;
        color: #6b7280;
        text-align: left;
        letter-spacing: 0.5px;
        @apply dark:text-slate-400;
      }

      &.action-cell {
        justify-content: space-between;
        gap: 8px;

        .action-buttons {
          display: flex;
          gap: 8px;
        }
      }
    }
  }
}

.table-footer-wrap {
  flex: 1;
  min-width: 0;
}

.table-footer-sticky {
  position: fixed;
  right: 0;
  bottom: 0;
  width: calc(100vw - 3.5rem);
  z-index: 3;
  flex-shrink: 0;
  margin-top: auto;
  background: #f9f9f9;
  border-top: 1px solid #e5e7eb;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 -1px 3px rgba(0, 0, 0, 0.05);

  @apply dark:bg-slate-800 dark:border-slate-700;
}

.no-data {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  font-size: 14px;
  color: #64748b;
  @apply dark:text-slate-400;
}
</style>
